import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Bike, Car, ChevronDown, Search } from "lucide-react";
import { BrandsInfiniteCarousel } from "./BrandsInfiniteCarousel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VehicleCategoryType, Brand } from "@/types";

type WizardType = "all" | VehicleCategoryType;

const HERO_WORDS = ["confianza", "respaldo", "calidad", "experiencia"] as const;
const WORD_INTERVAL_MS = 3000;

interface HeroWizardProps {
  brands: Brand[];
}

export function HeroWizard({ brands }: HeroWizardProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [type, setType] = useState<WizardType>("all");
  const [brandId, setBrandId] = useState<string>("all");
  const [budget, setBudget] = useState<string>("");
  const [wordIndex, setWordIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => {
      setWordIndex((i) => (i + 1) % HERO_WORDS.length);
    }, WORD_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  const orderedBrands = useMemo(
    () =>
      [...brands].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })
      ),
    [brands]
  );

  const goToCatalog = () => {
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (type !== "all") params.set("type", type);
    if (brandId !== "all") params.set("brand", brandId);
    if (budget.trim()) params.set("budget", budget.replace(/[^\d]/g, ""));
    navigate(`/catalogo?${params.toString()}`);
  };

  return (
    <section className="relative left-1/2 right-1/2 mb-8 w-screen min-h-[85vh] -translate-x-1/2 overflow-hidden text-white">
      {/* Background cinematográfico */}
      <div className="absolute inset-0">
        <img
          src="/mainbg.jpeg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0f1a]/80 via-[#1a1c3a]/70 to-[#0d0f1a]/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d0f1a]/60 to-transparent" />
      </div>

      {/* Ambient lights */}
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          className="absolute -left-32 top-1/4 h-[500px] w-[500px] rounded-full bg-[#E10E19]/15 blur-[120px]"
          animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -right-20 top-0 h-[400px] w-[400px] rounded-full bg-[#2563EB]/12 blur-[100px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.12, 0.2, 0.12] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      {/* Contenido */}
      <div className="relative mx-auto flex w-full max-w-[1400px] flex-col items-center justify-center px-4 pt-36 pb-12 sm:px-6 sm:pt-40 lg:px-10 lg:pt-44 lg:pb-16">
        {/* Headline principal */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-center text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
        >
          <span className="block">Tu próximo vehículo,</span>
          <span className="block mt-1">
            con{" "}
            <span className="relative inline-block">
              <AnimatePresence mode="wait">
                <motion.span
                  key={wordIndex}
                  initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -16, filter: "blur(6px)" }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-block bg-gradient-to-r from-[#FFD44D] via-[#FFB020] to-[#FF8C00] bg-clip-text text-transparent"
                >
                  {HERO_WORDS[wordIndex]}
                </motion.span>
              </AnimatePresence>
              <motion.span
                className="absolute -bottom-1 left-0 right-0 h-[3px] rounded-full bg-gradient-to-r from-[#FFD44D] to-[#FF8C00]"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              />
            </span>
          </span>
        </motion.h1>

        {/* Subtítulo */}
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-6 max-w-[52ch] text-center text-base leading-relaxed text-white/60 sm:text-lg"
        >
          Explora nuestro catálogo completo de motocicletas y automóviles.
          Financiamiento rápido con la inicial más baja del mercado.
        </motion.p>

        {/* Barra de búsqueda principal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-10 w-full max-w-[720px]"
        >
          <div className="relative flex items-center rounded-2xl border border-white/15 bg-white/8 p-2 shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-xl transition-all duration-300 focus-within:border-white/25 focus-within:bg-white/12 focus-within:shadow-[0_20px_60px_rgba(225,14,25,0.12)]">
            {/* Type selector pills */}
            <div className="hidden sm:flex items-center gap-1 pl-2 pr-3 border-r border-white/10">
              {(
                [
                  { value: "all" as const, label: "Todos" },
                  { value: VehicleCategoryType.MOTORCYCLE, label: "Motos", icon: Bike },
                  { value: VehicleCategoryType.AUTOMOBILE, label: "Carros", icon: Car },
                ] as const
              ).map((opt) => {
                const Icon = "icon" in opt ? opt.icon : null;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setType(opt.value)}
                    className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-all ${
                      type === opt.value
                        ? "bg-[#E10E19] text-white shadow-md shadow-[#E10E19]/25"
                        : "text-white/60 hover:text-white hover:bg-white/8"
                    }`}
                  >
                    {Icon && <Icon className="h-3.5 w-3.5" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Search input */}
            <div className="flex flex-1 items-center gap-2 px-3">
              <Search className="h-5 w-5 shrink-0 text-white/40" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && goToCatalog()}
                placeholder="Buscar marca, modelo, tipo..."
                className="h-11 w-full bg-transparent text-base font-medium text-white placeholder:text-white/35 focus:outline-none"
              />
            </div>

            {/* Search button */}
            <button
              type="button"
              onClick={goToCatalog}
              className="flex h-11 shrink-0 items-center gap-2 rounded-xl bg-[#E10E19] px-5 text-sm font-bold text-white shadow-lg shadow-[#E10E19]/20 transition-all hover:bg-[#C40D17] hover:shadow-xl hover:shadow-[#E10E19]/30 active:scale-[0.97]"
            >
              Buscar
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Filtros expandibles */}
          <div className="mt-2 flex justify-center">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white/50 transition-colors hover:text-white/70"
            >
              Filtros avanzados
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-300 ${
                  showFilters ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-2 grid grid-cols-1 gap-3 rounded-2xl border border-white/10 bg-white/6 p-4 backdrop-blur-xl sm:grid-cols-3">
                  {/* Mobile type selector */}
                  <div className="sm:hidden">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-white/50">
                      Tipo
                    </label>
                    <div className="flex gap-2">
                      {(
                        [
                          { value: "all" as const, label: "Todos" },
                          { value: VehicleCategoryType.MOTORCYCLE, label: "Motos" },
                          { value: VehicleCategoryType.AUTOMOBILE, label: "Carros" },
                        ] as const
                      ).map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setType(opt.value)}
                          className={`flex-1 rounded-xl py-2.5 text-sm font-semibold transition-all ${
                            type === opt.value
                              ? "bg-[#E10E19] text-white"
                              : "border border-white/15 text-white/60 hover:bg-white/8"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-white/50">
                      Marca
                    </label>
                    <Select value={brandId} onValueChange={setBrandId}>
                      <SelectTrigger className="h-11 rounded-xl border border-white/15 bg-white/5 px-4 text-sm font-medium text-white [&>span]:text-white">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las marcas</SelectItem>
                        {orderedBrands.map((brand) => (
                          <SelectItem key={brand.id} value={brand.id}>
                            {brand.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-white/50">
                      Presupuesto inicial
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      placeholder="RD$ 0"
                      className="h-11 w-full rounded-xl border border-white/15 bg-white/5 px-4 text-sm font-medium text-white placeholder:text-white/35 focus:border-[#E10E19]/50 focus:outline-none focus:ring-1 focus:ring-[#E10E19]/25"
                    />
                  </div>

                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={goToCatalog}
                      className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#E10E19] text-sm font-bold text-white transition-all hover:bg-[#C40D17]"
                    >
                      <Search className="h-4 w-4" />
                      Aplicar filtros
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Brands carousel */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-10 w-full max-w-[1100px]"
        >
          <BrandsInfiniteCarousel />
        </motion.div>
      </div>

      {/* Fade inferior */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0d0f1a] to-transparent" />
    </section>
  );
}
